import type { SetupGuide } from '@/features/setup-guide';
import type {
  ServicesApiGetCampaignsResponse,
  ServicesApiGetEnvironmentResponse,
  ServicesApiGetNewslettersResponse,
  ServicesApiGetTransactionalMessagesResponse,
  ServicesApiGetSegmentsResponse,
  ServicesApiTotalsResponse,
} from '@/services/api/services-api';

/**
 * Workspace resource counts assembled from existing endpoints.
 *
 * - campaignsCount, campaignsArchivedCount → getCampaigns meta.totals
 * - segmentsCount → getSegments response, excluding segments tagged "Sample"
 * - newslettersCount → getNewsletters meta.pagination.total (fallback: response length)
 * - transactionalCount → getTransactionalMessages response length (no pagination meta available)
 * - eventsCount → getEventNamesTotals totals.all
 * - customerAttributesCount → getAttributesTotals totals.all
 */
export interface WorkspaceCounts {
  campaignsCount: number;
  campaignsArchivedCount: number;
  segmentsCount: number;
  newslettersCount: number;
  transactionalCount: number;
  eventsCount: number;
  customerAttributesCount: number;
}

export interface WorkspaceContext {
  // From auth.account
  premium: boolean;
  premiumTrial: boolean;
  createdAt: number;
  featureFlags: Record<string, boolean>;

  // From getEnvironment response
  deliverySettings: string;
  enabledActions: string[];
  subscriptionCenterEnabled: boolean;
  languageAttribute: string;
  pushProviders: string[];
  objectTypesCount: number;

  // From setup guide
  integrationCompletedAt: number | null;

  // From getSegments (non-sample segment names for LLM context)
  segmentNames: string[];

  // Assembled from existing endpoints (null while loading)
  counts: WorkspaceCounts | null;
}

export function buildWorkspaceContext({
  account,
  environmentData,
  setupGuide,
  campaignsData,
  segmentsData,
  newslettersData,
  transactionalData,
  eventsTotalsData,
  attributesTotalsData,
}: {
  account: { premium: boolean; premium_trial: boolean; created_at: number; feature_flags: Record<string, boolean> };
  environmentData: ServicesApiGetEnvironmentResponse | null;
  setupGuide: SetupGuide | null;
  campaignsData: ServicesApiGetCampaignsResponse | null;
  segmentsData: ServicesApiGetSegmentsResponse | null;
  newslettersData: ServicesApiGetNewslettersResponse | null;
  transactionalData: ServicesApiGetTransactionalMessagesResponse | null;
  eventsTotalsData: ServicesApiTotalsResponse | null;
  attributesTotalsData: ServicesApiTotalsResponse | null;
}): WorkspaceContext {
  const env = environmentData?.environment ?? null;

  const hasCounts =
    campaignsData != null &&
    segmentsData != null &&
    newslettersData != null &&
    transactionalData != null &&
    eventsTotalsData != null &&
    attributesTotalsData != null;

  const nonSampleSegments = segmentsData != null ? getNonSampleSegments(segmentsData) : [];

  return {
    premium: account.premium,
    premiumTrial: account.premium_trial,
    createdAt: account.created_at,
    featureFlags: account.feature_flags,

    deliverySettings: env?.delivery_settings ?? '',
    enabledActions: env?.enabled_actions ?? [],
    subscriptionCenterEnabled: env?.subscription_center_enabled ?? false,
    languageAttribute: env?.language_attribute ?? '',
    pushProviders: env?.push_providers ?? [],
    objectTypesCount: environmentData?.object_types?.length ?? 0,

    integrationCompletedAt: setupGuide?.integration_completed_at ?? null,

    segmentNames: nonSampleSegments.map((s) => s.name),

    counts: hasCounts
      ? {
          campaignsCount: campaignsData.meta?.totals?.campaigns ?? 0,
          campaignsArchivedCount: campaignsData.meta?.totals?.archived ?? 0,
          segmentsCount: nonSampleSegments.length,
          newslettersCount: newslettersData.meta?.pagination?.total ?? newslettersData.newsletters.length,
          transactionalCount: transactionalData.transactional_messages.length,
          eventsCount: eventsTotalsData.totals.all,
          customerAttributesCount: attributesTotalsData.totals.all,
        }
      : null,
  };
}

/**
 * Return segments that are not tagged "Sample".
 *
 * Every new workspace gets 7–9 sample segments (e.g. "All Users",
 * "Paying Customers") which inflate segment counts and skew the
 * prompt card heuristics. The getSegments response includes a `tags`
 * array mapping tag IDs to names, and each segment has `tag_ids`.
 */
export function getNonSampleSegments(data: ServicesApiGetSegmentsResponse): ServicesApiGetSegmentsResponse['segments'] {
  const sampleTag = data.tags?.find((t) => t.name === 'Sample');
  if (sampleTag == null) {
    return data.segments;
  }
  const sampleTagId = sampleTag.id;
  return data.segments.filter((s) => {
    const tags = s.tag_ids ?? [];
    return !tags.includes(sampleTagId);
  });
}
